import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Building2, Check, ChevronDown, CreditCard, HelpCircle, LogOut, Plus, Settings, User } from "lucide-react";
import { api, API_URL } from "../lib/api";

export default function ProfileMenu({ compact = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showBusinesses, setShowBusinesses] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [account, setAccount] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const ref = useRef(null);

  const loadAccount = () => api.get("/account/me", { withCredentials: true }).then((r) => setAccount(r.data)).catch(() => setAccount(null));
  const loadBusinesses = () => api.get("/businesses", { withCredentials: true }).then((r) => setBusinesses(r.data || [])).catch(() => setBusinesses([]));

  useEffect(() => { loadAccount(); loadBusinesses(); }, []);

  useEffect(() => {
    if (!account) return;
    if (account.avatar_uploaded) {
      const token = window.localStorage.getItem("session_token");
      fetch(`${API_URL}/account/avatar`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => blob && setAvatarUrl(URL.createObjectURL(blob)))
        .catch(() => setAvatarUrl(""));
    } else if (account.picture) {
      setAvatarUrl(account.picture);
    }
  }, [account?.avatar_uploaded, account?.picture]);

  useEffect(() => {
    const onClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
        setShowBusinesses(false);
        setShowCreate(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch (_) { /* noop */ }
    window.localStorage.removeItem("session_token");
    navigate("/", { replace: true });
    window.location.reload();
  };

  const activate = async (id) => {
    try {
      await api.put(`/businesses/${id}/activate`, {}, { withCredentials: true });
      window.location.reload();
    } catch (_) { /* noop */ }
  };

  const create = async (event) => {
    event.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/businesses", { business_name: newName.trim() }, { withCredentials: true });
      setNewName("");
      setShowCreate(false);
      await loadBusinesses();
      navigate("/perfil-negocio");
      window.location.reload();
    } catch (_) {
      setCreating(false);
    }
  };

  const initials = (account?.name || account?.email || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const activeBusiness = businesses.find((b) => b.is_active) || businesses[0];

  const items = [
    { icon: User, label: "Mi Perfil", to: "/mi-perfil", testid: "profile-menu-my-profile" },
    { icon: Building2, label: "Perfil de Negocio", to: "/perfil-negocio", testid: "profile-menu-business" },
    { icon: Settings, label: "Ajustes de cuenta", to: "/mi-perfil?tab=ajustes", testid: "profile-menu-settings" },
    { icon: Bell, label: "Notificaciones", to: "/mi-perfil?tab=notificaciones", testid: "profile-menu-notifications" },
    { icon: CreditCard, label: "Mi Suscripción", to: "/mi-suscripcion", testid: "profile-menu-subscription" },
    { icon: HelpCircle, label: "Centro de ayuda", to: "/mi-perfil?tab=ayuda", testid: "profile-menu-help" },
  ];

  return (
    <div className={`profile-menu${compact ? " profile-menu-compact" : ""}`} ref={ref} data-testid="profile-menu">
      <button type="button" className="profile-menu-trigger" onClick={() => setOpen((v) => !v)} data-testid="profile-menu-trigger" aria-label="Abrir menú de perfil">
        <span className="profile-menu-avatar" data-testid="profile-menu-avatar">
          {avatarUrl ? <img src={avatarUrl} alt="" /> : <b>{initials || <User size={16} />}</b>}
        </span>
        {!compact && <>
          <span className="profile-menu-name">{activeBusiness?.business_name || account?.name?.split(" ")[0] || "Cuenta"}</span>
          <ChevronDown size={15} />
        </>}
      </button>
      {open && (
        <div className="profile-menu-pop" data-testid="profile-menu-popover">
          <header className="profile-menu-head">
            <span className="profile-menu-avatar large">
              {avatarUrl ? <img src={avatarUrl} alt="" /> : <b>{initials}</b>}
            </span>
            <div>
              <b>{account?.name || "Sin nombre"}</b>
              <small>{account?.email}</small>
              <em>{account?.subscription?.plan?.toUpperCase() || "FREE"} · {businesses.length} empresa{businesses.length === 1 ? "" : "s"}</em>
            </div>
          </header>

          <button
            type="button"
            className="profile-menu-business-switcher"
            onClick={() => setShowBusinesses((v) => !v)}
            data-testid="profile-menu-business-switcher"
          >
            <Building2 size={16} />
            <span>
              <b>{activeBusiness?.business_name || "Sin empresa activa"}</b>
              <small>Cambiar de empresa</small>
            </span>
            <ChevronDown size={14} className={showBusinesses ? "rotated" : ""} />
          </button>

          {showBusinesses && (
            <div className="profile-menu-businesses" data-testid="profile-menu-businesses">
              {businesses.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={b.is_active ? "active" : ""}
                  onClick={() => activate(b.id)}
                  data-testid={`profile-menu-business-${b.id}`}
                >
                  <span className="profile-menu-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                    <b>{(b.business_name || "??").split(" ").map((n) => n[0]).slice(0, 2).join("")}</b>
                  </span>
                  <span className="profile-menu-business-info">
                    <b>{b.business_name}</b>
                    <small>{b.rfc || "Sin RFC"}</small>
                  </span>
                  {b.is_active && <Check size={16} className="profile-menu-check" />}
                </button>
              ))}
              {showCreate ? (
                <form onSubmit={create} className="profile-menu-new-business" data-testid="profile-menu-new-form">
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nombre de la empresa"
                    data-testid="profile-menu-new-name"
                  />
                  <button type="submit" disabled={creating || !newName.trim()} data-testid="profile-menu-new-submit">
                    {creating ? "Creando..." : "Crear"}
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  className="profile-menu-new-trigger"
                  onClick={() => setShowCreate(true)}
                  data-testid="profile-menu-add-business"
                >
                  <Plus size={14} /> Añadir empresa
                </button>
              )}
            </div>
          )}

          <nav>
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => { setOpen(false); navigate(item.to); }}
                data-testid={item.testid}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
                {item.badge && <em className="profile-menu-badge">{item.badge}</em>}
              </button>
            ))}
          </nav>
          <nav className="profile-menu-legal-group" data-testid="profile-menu-legal-group">
            <div className="profile-menu-legal-title">Legal</div>
            <button type="button" onClick={() => { setOpen(false); navigate("/legal/privacidad"); }} data-testid="profile-menu-privacy">
              <span style={{ width: 16 }} /> <span>Aviso de Privacidad</span>
            </button>
            <button type="button" onClick={() => { setOpen(false); navigate("/legal/terminos"); }} data-testid="profile-menu-terms">
              <span style={{ width: 16 }} /> <span>Términos y Condiciones</span>
            </button>
            <button type="button" onClick={() => { setOpen(false); navigate("/legal/cookies"); }} data-testid="profile-menu-cookies">
              <span style={{ width: 16 }} /> <span>Política de Cookies</span>
            </button>
          </nav>
          <button type="button" className="profile-menu-logout" onClick={logout} data-testid="profile-menu-logout">
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
